import { bem, selectSpeakerSinkId } from '@socialive/common';
import PropTypes from 'prop-types';
import { forwardRef, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import './videoStream.css';

/*
#tailwind
videoStream videoStream--isMirrored  videoStream--isMasked videoStream--isHidden
videoStream--hidePlayButton videoStream--hideTimeline videoStream--hideCurrentTimeDisplay
*/

const VideoStream = forwardRef(
  (
    {
      className,
      stream,
      isHidden,
      isMasked,
      isMirrored,
      isMuted,
      autoPlay,
      hidePlayButton,
      hideTimeline,
      hideCurrentTimeDisplay,
      testId,
      ...other
    },
    externalRef
  ) => {
    const internalRef = useRef();
    const ref = useMemo(() => externalRef ?? internalRef, [internalRef, externalRef]);

    const sinkId = useSelector(selectSpeakerSinkId);

    useEffect(
      /*
       * NOTE: When a <video> element is unmounted, Chrome+React does not properly dispose of the media stream.
       * You can see this by viewing DevTools > Media and verifying that the media receives a kWebMediaPlayerCreated event
       * but never receives a kWebMediaPlayerDestroyed. The next time the same video src comes through a new media is
       * created, which is a memory leak. On unmount we are going to have the <video> element clean up after itself.
       */
      () => {
        const { current } = ref;
        if (current) {
          return () => {
            if (current) {
              current.pause();
              current.src = '';
              current.load();
            }
          };
        }
        return undefined;
      },
      [ref]
    );

    useEffect(() => {
      ref.current.srcObject = stream;
      // timeout video display till it is loaded (GXP-783)
      setTimeout(() => ref.current?.style.removeProperty('visibility'), 16);
    }, [stream, ref]);

    useEffect(() => {
      const target = ref.current;
      const play = () => target.paused && target.play();
      target?.addEventListener('canplay', play);
      return () => target?.removeEventListener('canplay', play);
    }, [stream, ref]);

    const updateSinkId = useCallback(() => {
      if (ref.current?.srcObject?.getAudioTracks().length && sinkId) {
        ref.current.setSinkId(sinkId);
      }
    }, [sinkId, ref]);

    useEffect(() => {
      updateSinkId();
      stream?.addEventListener('addtrack', updateSinkId);
      return () => stream?.removeEventListener('addtrack', updateSinkId);
    }, [stream, updateSinkId]);

    return (
      <video
        data-testid={testId}
        style={{ visibility: 'hidden' }}
        className={bem`videoStream--${{
          isMirrored,
          hidePlayButton,
          hideTimeline,
          hideCurrentTimeDisplay,
          isMasked,
          isHidden,
        }} ${className}`}
        ref={ref}
        muted={isMuted}
        autoPlay={autoPlay}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...other}
      />
    );
  }
);

VideoStream.propTypes = {
  className: PropTypes.string,
  isHidden: PropTypes.bool,
  isMasked: PropTypes.bool,
  isMirrored: PropTypes.bool,
  stream: PropTypes.instanceOf(MediaStream),
  isMuted: PropTypes.bool,
  autoPlay: PropTypes.bool,
  hidePlayButton: PropTypes.bool,
  hideTimeline: PropTypes.bool,
  hideCurrentTimeDisplay: PropTypes.bool,
  testId: PropTypes.string,
};

VideoStream.defaultProps = {
  className: undefined,
  isHidden: false,
  isMasked: false,
  isMirrored: false,
  stream: undefined,
  isMuted: false,
  autoPlay: false,
  hidePlayButton: false,
  hideTimeline: false,
  hideCurrentTimeDisplay: false,
  testId: undefined,
};

export default VideoStream;
